import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import HomeShortcutService from 'service/HomeShortcut/HomeShortcutService';
import { HomeShortcutItem } from '../../domain/payload/response/homeShortcut/HomeShortcutResp';
import { hasHomeShortcutAccess } from 'helpers/hasPrivilege';
import CustomDropdown from 'component/CustomDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';

const HomeShortcut: React.FC = () => {
  const { profile } = useSelector((state: any) => state.auth);
  const [data, setData] = useState<HomeShortcutItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const homeShortcutService = HomeShortcutService();

  const canCreate = hasHomeShortcutAccess(profile, 'create');
  const canUpdate = hasHomeShortcutAccess(profile, 'update');

  const fetchData = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setLoading(true);
    try {
      const res = await homeShortcutService.getHomeShortcutList(
        token,
        searchTerm,
        currentPage,
        pageSize
      );
      console.log('HomeShortcut list response:', res);
      const sortedData = (res.data ?? []).sort((a, b) => a.sort - b.sort);
      setData(sortedData);
      setTotalPages(res.pagination?.totalPage ?? 0);
      setTotalRecords(res.pagination?.totalRecords ?? 0);
    } catch (err: any) {
      console.error(err);
      const errorMessage = typeof err === 'string' ? err : err.message || 'Terjadi kesalahan saat mengambil data home shortcut';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchData();
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setIsDragging(false);

    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    if (dragIndex === dropIndex) return;

    const newData = [...data];
    const draggedItem = newData[dragIndex];
    newData.splice(dragIndex, 1);
    newData.splice(dropIndex, 0, draggedItem);

    const updatedItems = newData.map((item, idx) => ({
      id: item.id,
      sort: idx + 1
    }));

    setData(newData);

    const token = profile?.data.token;
    if (token) {
      try {
        await homeShortcutService.updateSort(token, { items: updatedItems });
        fetchData();
      } catch (err: any) {
        console.error('Failed to update sort:', err);
        window.alert('Gagal menyimpan urutan. Silakan coba lagi.');
        fetchData();
      }
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive
      ? <span className="badge bg-success">Active</span>
      : <span className="badge bg-danger">Inactive</span>;
  };

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex">
            <h5 style={{ flex: 1 }}>Home Shortcut</h5>
            <div>
              <p>TransMore Mobile Shortcut</p>
            </div>
          </div>
        </div>
        <div className="col-12">

          <div className="p-4" style={{ background: 'white' }}>
            <div className="mb-3 d-flex">

              <form className="d-flex" onSubmit={handleSearchClick}>
                <input
                  type="text"
                  className="form-control form-control-compact me-2"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button type="submit" className="px-4 btn btn-primary">
                  <i className="fa-solid fa-search"></i>
                </button>
              </form>

              <div className="d-flex justify-content-end" style={{ flex: 1 }}>
                {canCreate && (
                  <Link to="/mobile-shortcut/0" className='float-end text-decoration-none'>
                    <button className="btn btn-primary">
                      <span className="me-2">Add</span>
                      <i className="fa-solid fa-plus"></i>
                    </button>
                  </Link>
                )}
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-condensed table-hover">
                <thead className="table-primary">
                  <tr>
                    <th style={{ width: '50px' }}>Sort</th>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Created At</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {!loading ? (
                    data.length > 0 ? (
                      data.map((item, index) => (
                        <tr
                          key={item.id}
                          draggable={canUpdate}
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragEnd={handleDragEnd}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, index)}
                          style={{
                            cursor: canUpdate ? 'grab' : 'default',
                            opacity: isDragging ? 0.5 : 1,
                            backgroundColor: isDragging ? '#f8f9fa' : 'transparent'
                          }}
                        >
                          <td style={{ cursor: 'grab' }}>
                            <i className="fa-solid fa-grip-vertical text-muted me-2"></i>
                            {item.sort}
                          </td>
                          <td>{item.id}</td>
                          <td>{item.name}</td>
                          <td>{item.type}</td>
                          <td>{getStatusBadge(item.isActive)}</td>
                          <td>{item.createdAt}</td>
                          <td>
                            <div className="d-flex">
                              {canUpdate && (
                                <Link to={`/mobile-shortcut/${item.id}`}>
                                  <button className="border-0 btn btn-outline-warning btn-small-compact">
                                    <i className="fa-solid fa-edit"></i>
                                  </button>
                                </Link>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="text-center">
                          Tidak ada data
                        </td>
                      </tr>
                    )
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-3 d-flex justify-content-end align-items-center">
              <div className="d-flex align-items-center">
                <div className="me-4 d-flex align-items-center">
                  <label className="me-2">Rows per page:</label>
                  <CustomDropdown
                    value={pageSize}
                    options={PageSizeOption.options}
                    onChange={(opt) => {
                      setPageSize(Number(opt.id));
                      setCurrentPage(1);
                    }}
                    align="top"
                  />
                </div>
                <span className="me-3">
                  {`${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`}
                </span>

                <nav>
                  <ul className="mb-0 pagination">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-left"></i>
                      </button>
                    </li>

                    <li className="page-item disabled">
                      <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                    </li>

                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(totalPages)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeShortcut;
